'use client';

import { useRef, useState, useCallback, useEffect } from 'react';
import { Link } from '@medizzconnect/i18n';
import { useTranslations, useLocale } from 'next-intl';
import { useAuth } from '@medizzconnect/auth';
import { cn } from '@medizzconnect/shared';
import { getPlanFeatures } from '@medizzconnect/shared';
import { useAutoplayUnlock, robustUnmute } from '@/lib/use-autoplay-unlock';
import { getBestVideoUrl } from '@/lib/video-url';
import { FavoriteButton } from '@/components/doctors/favorite-button';
import { prefetchVideoProfile } from '@/lib/video-profile-cache';
import { ResilientImage } from '@/components/ui/resilient-image';
import type { VideoCardData } from './use-videos';

interface VideoCardProps {
  profile: VideoCardData;
  className?: string;
  priority?: boolean;
  /** Wenn gesetzt, wird onClick statt Link verwendet (für Modal-Öffnung) */
  onSelect?: (profileId: string) => void;
  /** Ob Mock-Profile per Klick im Modal geöffnet werden dürfen */
  mockClickEnabled?: boolean;
}

/**
 * Innerer Inhalt der Video-Card (Thumbnail, Badge, Video).
 * isActive = true → Video einblenden (Hover auf Desktop, InView auf Mobile).
 */
function VideoCardInner({
  profile,
  thumbnailUrl,
  displayName,
  isActive,
  isVideoReady,
  isAudioUnlocked,
  videoRef,
  priority,
  setIsVideoReady,
}: {
  profile: VideoCardData;
  thumbnailUrl: string;
  displayName: string;
  isActive: boolean;
  isVideoReady: boolean;
  isAudioUnlocked: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  priority: boolean;
  setIsVideoReady: (v: boolean) => void;
}) {
  return (
    <>
      {/* Thumbnail */}
      <ResilientImage
        src={thumbnailUrl}
        alt={displayName}
        fill
        sizes="(max-width:640px) 50vw, (max-width:768px) 25vw, (max-width:1024px) 25vw, 20vw"
        className={cn(
          'object-cover transition-opacity duration-300',
          isActive && isVideoReady ? 'opacity-0' : 'opacity-100',
        )}
        priority={priority}
      />

      {/* Plan Badge */}
      {(() => {
        const planFeatures = getPlanFeatures(profile.plan);
        if (planFeatures.id === 'basis') return null;
        return (
          <span
            className="absolute top-1.5 left-1.5 z-10 rounded-sm px-1 py-0.5 text-[7px] font-semibold uppercase tracking-wide text-white shadow-sm sm:px-2 sm:py-0.5 sm:text-[10px]"
            style={{
              background: planFeatures.hasPremiumBadge
                ? 'linear-gradient(135deg, #38b2ac, #2c7a7b)'
                : 'linear-gradient(135deg, #4299e1, #3182ce)',
            }}
          >
            {planFeatures.label}
          </span>
        );
      })()}

      {/* Klinik-Badge 
      
          {profile.is_clinic_member && (
        <span className="absolute top-2 right-2 z-10 flex items-center gap-1 rounded-sm bg-white/90 backdrop-blur-sm px-1.5 py-0.5 text-[8px] font-medium text-teal-700 shadow sm:px-2 sm:py-0.5 sm:text-[10px]">
          <svg className="h-2.5 w-2.5 sm:h-3 sm:w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
          </svg>
          Klinik
        </span>
      )}
      */}

      {/* Video */}
      {getBestVideoUrl(profile) && (
        <video
          ref={videoRef}
          src={getBestVideoUrl(profile) ?? undefined}
          className={cn(
            'pointer-events-none absolute inset-0 h-full w-full select-none object-cover transition-opacity duration-300',
            isActive && isVideoReady ? 'opacity-100' : 'opacity-0',
          )}
          draggable={false}
          muted
          loop
          playsInline
          preload="none"
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload noremoteplayback"
          onContextMenu={(e) => e.preventDefault()}
          onCanPlay={() => setIsVideoReady(true)}
        />
      )}
    </>
  );
}

/**
 * V1-Style Video-Card.
 * Desktop: Hover-to-Play.
 * Mobile  (<640 px, Touch): Scroll-to-Play via IntersectionObserver (TikTok-Style).
 */
export function VideoCard({ profile, className, priority = false, onSelect, mockClickEnabled = false }: VideoCardProps) {
  const t = useTranslations('home');
  const { userId } = useAuth();
  const locale = useLocale();
  const videoRef = useRef<HTMLVideoElement>(null);
  const articleRef = useRef<HTMLElement>(null);

  // Desktop-Hover-State
  const [isHovering, setIsHovering] = useState(false);
  // Mobile InView-State (IntersectionObserver)
  const [isMobileActive, setIsMobileActive] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  // Ob wir gerade auf einem Touch-/Mobile-Gerät sind
  const [isMobile, setIsMobile] = useState(false);

  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAudioUnlocked = useAutoplayUnlock();
  // Ref für stabilen Zugriff in IntersectionObserver-Callback
  const isAudioUnlockedRef = useRef(isAudioUnlocked);
  useEffect(() => { isAudioUnlockedRef.current = isAudioUnlocked; }, [isAudioUnlocked]);

  const thumbnailUrl = profile.thumbnail_url ?? '/images/default-video-thumbnail.jpg';
  const displayName = [profile.title, profile.first_name, profile.last_name]
    .filter(Boolean)
    .join(' ');

  // ── Mobile-Erkennung (client-only) ──
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // ── Sound-Allowed Listener: Unmute aktuell spielendes Video nach Consent ──
  useEffect(() => {
    const onSoundAllowed = () => {
      const video = videoRef.current;
      if (video && !video.paused && video.muted) {
        robustUnmute(video);
      }
    };
    document.addEventListener('sound-allowed', onSoundAllowed);
    return () => document.removeEventListener('sound-allowed', onSoundAllowed);
  }, []);

  // ── Mobile: IntersectionObserver – spielt Video wenn ≥55 % sichtbar ──
  // Gleiche Audio-Regeln wie Desktop-Hover:
  //   • Video startet immer stumm (Browser-Policy)
  //   • isAudioUnlocked (= echte User-Geste in dieser Session) → sofort unmuten
  //   • sonst → Audio-Consent-Dialog anfordern
  //   • 'sound-allowed'-Event (aus Dialog) → laufendes Video unmuten (bereits oben registriert)
  useEffect(() => {
    if (!isMobile) return;
    const el = articleRef.current;
    if (!el || !getBestVideoUrl(profile)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const video = videoRef.current;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
          setIsMobileActive(true);
          // Debounce: bei schnellem Scrollen soll eine nur kurz sichtbare Card
          // keinen Prefetch/Video-Request auslösen (vermeidet Request-Bursts).
          mobileTimeoutRef.current = setTimeout(() => {
            // Profildaten für Modal prefetchen während User die Card sieht
            if (onSelect && !profile.is_mock) prefetchVideoProfile(profile.id, locale);
            if (video) {
              video.currentTime = 0;
              video.muted = true;
              video.play().then(() => {
                // Andere Cards stoppen
                document.dispatchEvent(new CustomEvent('video-card-playing', { detail: { id: profile.id } }));
                // isAudioUnlockedRef: Ref-Zugriff statt Closure (Observer wird nur einmal erstellt)
                if (isAudioUnlockedRef.current) {
                  robustUnmute(video);
                } else {
                  document.dispatchEvent(new Event('request-audio-consent'));
                }
              }).catch(() => {});
            }
          }, 250);
        } else {
          if (mobileTimeoutRef.current) {
            clearTimeout(mobileTimeoutRef.current);
            mobileTimeoutRef.current = null;
          }
          setIsMobileActive(false);
          setIsVideoReady(false);
          if (video) {
            video.muted = true;
            video.pause();
            video.currentTime = 0;
          }
        }
      },
      { threshold: [0.0, 0.55] },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (mobileTimeoutRef.current) {
        clearTimeout(mobileTimeoutRef.current);
        mobileTimeoutRef.current = null;
      }
    };
  }, [isMobile, profile.id]); // profile.id als stabiler Dep

  // ── Desktop: Hover-Handler ──
  const handleMouseEnter = useCallback(() => {
    // Profildaten für Modal bereits beim Hover prefetchen
    if (onSelect && !profile.is_mock) prefetchVideoProfile(profile.id, locale);
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
      const video = videoRef.current;
      if (video && profile.video_url) {
        video.currentTime = 0;
        video.muted = true;
        video.play().then(() => {
          // Andere Cards stoppen
          document.dispatchEvent(new CustomEvent('video-card-playing', { detail: { id: profile.id } }));
          if (isAudioUnlocked) {
            robustUnmute(video);
          } else {
            document.dispatchEvent(new Event('request-audio-consent'));
          }
        }).catch(() => {});
      }
    }, 50);
  }, [profile.id, profile.is_mock, profile.video_url, locale, onSelect, isAudioUnlocked]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (mobileTimeoutRef.current) {
      clearTimeout(mobileTimeoutRef.current);
      mobileTimeoutRef.current = null;
    }
    setIsHovering(false);
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.pause();
      video.currentTime = 0;
    }
  }, []);

  // Aktiv = Hover (Desktop) ODER InView (Mobile)
  const isActive = isMobile ? isMobileActive : isHovering;

  // Gemeinsames Stop-Video (vor Modal-Öffnung o.ä.)
  const stopVideo = useCallback(() => {
    if (mobileTimeoutRef.current) {
      clearTimeout(mobileTimeoutRef.current);
      mobileTimeoutRef.current = null;
    }
    const video = videoRef.current;
    if (video) { video.pause(); video.currentTime = 0; video.muted = true; }
    setIsHovering(false);
    setIsMobileActive(false);
  }, []);

  // Ref für stabile Nutzung im Event-Listener ohne stale-Closure
  const stopVideoRef = useRef(stopVideo);
  useEffect(() => { stopVideoRef.current = stopVideo; }, [stopVideo]);

  // ── Exklusiv-Play: wenn eine andere Card startet → diese stoppen ──
  useEffect(() => {
    const onOtherCardPlaying = (e: Event) => {
      const { id } = (e as CustomEvent<{ id: string }>).detail;
      if (id !== profile.id) stopVideoRef.current();
    };
    document.addEventListener('video-card-playing', onOtherCardPlaying);
    return () => document.removeEventListener('video-card-playing', onOtherCardPlaying);
  }, [profile.id]);

  // Aspekt-Ratio wird per CSS gesteuert (aspect-[3/4] sm:aspect-[5/4]) – kein JS, kein CLS

  return (
    <article
      ref={articleRef as React.RefObject<HTMLDivElement>}
      className={cn(
        'group relative flex w-full cursor-pointer flex-col overflow-hidden bg-white text-left shadow-sm transition hover:-translate-y-1 hover:shadow-lg',
        className,
      )}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onContextMenuCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onMouseDownCapture={(e) => {
        if (e.button === 2) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      // Hover nur auf Desktop
      onMouseEnter={isMobile ? undefined : handleMouseEnter}
      onMouseLeave={isMobile ? undefined : handleMouseLeave}
    >
      {/* ── Video / Thumbnail ── */}
      {profile.is_mock && !mockClickEnabled ? (
        <div
          className="relative block w-full overflow-hidden bg-slate-200 aspect-[3/4] sm:aspect-[5/4]"
          aria-label={displayName}
        >
          <VideoCardInner
            profile={profile}
            thumbnailUrl={thumbnailUrl}
            displayName={displayName}
            isActive={isActive}
            isVideoReady={isVideoReady}
            isAudioUnlocked={isAudioUnlocked}
            videoRef={videoRef}
            priority={priority}
            setIsVideoReady={setIsVideoReady}
          />
        </div>
      ) : onSelect ? (
        <div
          onClick={() => { stopVideo(); onSelect(profile.id); }}
          className="relative block w-full overflow-hidden bg-slate-200 cursor-pointer aspect-[3/4] sm:aspect-[5/4]"
          role="button"
          tabIndex={0}
          aria-label={`${displayName} – ${t('videos.playVideo')}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              stopVideo();
              onSelect(profile.id);
            }
          }}
        >
          <VideoCardInner
            profile={profile}
            thumbnailUrl={thumbnailUrl}
            displayName={displayName}
            isActive={isActive}
            isVideoReady={isVideoReady}
            isAudioUnlocked={isAudioUnlocked}
            videoRef={videoRef}
            priority={priority}
            setIsVideoReady={setIsVideoReady}
          />
        </div>
      ) : (
        <Link
          href={`/doctors/${profile.id}`}
          className="relative block w-full overflow-hidden bg-slate-200 aspect-[3/4] sm:aspect-[5/4]"
          aria-label={`${displayName} – ${t('videos.playVideo')}`}>

          <VideoCardInner
            profile={profile}
            thumbnailUrl={thumbnailUrl}
            displayName={displayName}
            isActive={isActive}
            isVideoReady={isVideoReady}
            isAudioUnlocked={isAudioUnlocked}
            videoRef={videoRef}
            priority={priority}
            setIsVideoReady={setIsVideoReady}
          />
        </Link>
      )}

      {/* Favorit-Button */}
      {!profile.is_mock && userId && (
        <div className="absolute bottom-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <FavoriteButton profileId={profile.id} userId={userId} compact />
        </div>
      )}
    </article>
  );
}
